import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/auth'
import { supabase } from '@/lib/supabase'
import type { BillingStatus } from '@/types/billing'

export function useBillingStatus() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['billing-status', user?.id],
    queryFn: async (): Promise<BillingStatus> => {
      if (!user) {
        return {
          isExempt: false,
          isPaid: false,
          hasAccess: false,
        }
      }

      // Check for active billing exemption using the SQL function
      const { data: isExempt, error: exemptError } = await supabase.rpc('is_user_billing_exempt', {
        p_user_id: user.id,
      })

      if (exemptError) {
        console.warn('Error checking exemption:', exemptError)
      }

      if (isExempt) {
        // Get exemption details if user is exempt
        const { data: exemption } = await supabase
          .from('billing_exemptions')
          .select('reason, expires_at')
          .eq('user_id', user.id)
          .is('revoked_at', null)
          .single()

        return {
          isExempt: true,
          exemptReason: exemption?.reason,
          exemptExpiresAt: exemption?.expires_at ? new Date(exemption.expires_at) : undefined,
          isPaid: true, // Exempt users are considered "paid"
          hasAccess: true,
        }
      }

      // Calculate trial period (14 days from user creation)
      const createdAt = new Date(user.created_at)
      const trialEndsAt = new Date(createdAt.getTime() + 14 * 24 * 60 * 60 * 1000)
      const isInTrial = new Date() < trialEndsAt

      // Check for active subscription
      const { data: subscription } = await supabase.rpc('get_user_active_subscription', {
        p_user_id: user.id,
      })

      // The RPC returns an array, so we need to get the first item
      const activeSubscription = subscription && subscription.length > 0 ? subscription[0] : null

      if (activeSubscription) {
        const subscription = {
          id: activeSubscription.external_id,
          status: activeSubscription.status as
            | 'active'
            | 'canceled'
            | 'past_due'
            | 'unpaid'
            | 'trialing',
          plan: activeSubscription.plan_type as 'monthly' | 'annual',
          currentPeriodEnd: new Date(activeSubscription.current_period_end),
          cancelAtPeriodEnd: activeSubscription.cancel_at_period_end,
        }

        // Handle different subscription statuses
        switch (activeSubscription.status) {
          case 'active':
          case 'trialing':
            return {
              isExempt: false,
              trialEndsAt: isInTrial ? trialEndsAt : undefined,
              isPaid: true,
              hasAccess: true,
              subscription,
            }

          case 'past_due':
            // Grace period for past_due - still has access but with warning
            return {
              isExempt: false,
              trialEndsAt: isInTrial ? trialEndsAt : undefined,
              isPaid: false,
              hasAccess: true, // Grace period
              pastDue: true,
              subscription,
            }

          case 'canceled': {
            // Check if canceled subscription still has time left
            const now = new Date()
            const periodEnd = new Date(activeSubscription.current_period_end)
            const hasTimeLeft = now < periodEnd

            return {
              isExempt: false,
              trialEndsAt: isInTrial ? trialEndsAt : undefined,
              isPaid: hasTimeLeft,
              hasAccess: hasTimeLeft,
              canceled: true,
              canceledAt: hasTimeLeft ? periodEnd : undefined,
              subscription,
            }
          }

          default:
            // unpaid or other statuses - no access
            return {
              isExempt: false,
              trialEndsAt: isInTrial ? trialEndsAt : undefined,
              isPaid: false,
              hasAccess: false,
              subscription,
            }
        }
      }

      // Fall back to trial logic
      return {
        isExempt: false,
        trialEndsAt,
        isPaid: isInTrial,
        hasAccess: isInTrial,
      }
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}

// Simple hook to check if user has access
export function useHasBillingAccess() {
  const { data: billingStatus } = useBillingStatus()
  return billingStatus?.hasAccess ?? false
}

// Hook to check if user is exempt
export function useIsUserExempt() {
  const { data: billingStatus } = useBillingStatus()
  return {
    isExempt: billingStatus?.isExempt ?? false,
    reason: billingStatus?.exemptReason,
    expiresAt: billingStatus?.exemptExpiresAt,
  }
}
